export interface PDFExportColumn {
  title: string;
  dataIndex: string;
  render?: (val: any, record: any) => string;
}

export interface PDFExportOptions {
  title: string;
  subtitle?: string;
  columns: PDFExportColumn[];
  data: any[];
  fileName?: string;
}

export const exportToPDF = ({
  title,
  subtitle = "Delhi Metro Rail Corporation Ltd.",
  columns,
  data,
  fileName = "DMRC_DAR_Report"
}: PDFExportOptions) => {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Please allow popups to export PDF");
    return;
  }

  const currentDate = new Date().toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short"
  });

  const tableHeaders = columns
    .map(col => `<th>${col.title}</th>`)
    .join("");

  const tableRows = data
    .map(row => {
      const cells = columns
        .map(col => {
          let value = row[col.dataIndex];
          if (col.render) {
            const rendered = col.render(value, row);
            // If rendered is a React node string or primitive, extract text if possible
            if (typeof rendered === "string") {
              // Strip HTML tags for clean text rendering in PDF table if necessary
              value = rendered.replace(/<[^>]*>?/gm, "");
            } else {
              value = String(rendered || "-");
            }
          }
          if (value === undefined || value === null || value === "") {
            value = "-";
          }
          return `<td>${value}</td>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  const htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title} - ${fileName}</title>
        <style>
          @page {
            size: A4 landscape;
            margin: 12mm;
          }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: #333;
            margin: 0;
            padding: 20px;
            background-color: #fff;
          }
          .report-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 3px solid #800000;
            padding-bottom: 12px;
            margin-bottom: 20px;
          }
          .brand-title {
            font-size: 22px;
            font-weight: 700;
            color: #800000;
            letter-spacing: 0.5px;
            margin: 0;
          }
          .sub-brand {
            font-size: 13px;
            color: #555;
            margin-top: 4px;
            font-weight: 500;
          }
          .meta-info {
            text-align: right;
            font-size: 12px;
            color: #666;
          }
          .meta-info strong {
            color: #222;
          }
          .report-title-bar {
            background-color: #f8f9fa;
            border-left: 4px solid #002b49;
            padding: 10px 15px;
            margin-bottom: 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .report-title-bar h2 {
            margin: 0;
            font-size: 16px;
            color: #002b49;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .record-count {
            background-color: #002b49;
            color: #fff;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
            margin-top: 10px;
          }
          th {
            background-color: #002b49;
            color: #ffffff;
            text-align: left;
            padding: 10px 12px;
            font-weight: 600;
            border: 1px solid #002b49;
            text-transform: uppercase;
            font-size: 10px;
            letter-spacing: 0.5px;
          }
          td {
            padding: 9px 12px;
            border: 1px solid #e0e0e0;
            color: #333;
          }
          tr:nth-child(even) {
            background-color: #fdfdfd;
          }
          tr:nth-child(odd) {
            background-color: #ffffff;
          }
          .footer {
            margin-top: 30px;
            border-top: 1px solid #ddd;
            padding-top: 10px;
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            color: #777;
          }
          @media print {
            body {
              padding: 0;
            }
            .no-print {
              display: none !important;
            }
            table {
              page-break-inside: auto;
            }
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div>
            <h1 class="brand-title">DELHI METRO RAIL CORPORATION LTD.</h1>
            <div class="sub-brand">Human Resource Management System (HRMS) - D&AR Section</div>
          </div>
          <div class="meta-info">
            <div><strong>Date:</strong> ${currentDate}</div>
            <div><strong>Status:</strong> Confidential / Official</div>
          </div>
        </div>

        <div class="report-title-bar">
          <h2>${title}</h2>
          <span class="record-count">Total Records: ${data.length}</span>
        </div>

        <table>
          <thead>
            <tr>${tableHeaders}</tr>
          </thead>
          <tbody>
            ${tableRows.length > 0 ? tableRows : '<tr><td colspan="' + columns.length + '" style="text-align:center; padding: 20px;">No records available</td></tr>'}
          </tbody>
        </table>

        <div class="footer">
          <div>Report generated automatically via DMRC HRMS Portal</div>
          <div>Page 1 of 1</div>
        </div>

        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
    </html>
  `;

  printWindow.document.open();
  printWindow.document.write(htmlContent);
  printWindow.document.close();
};
